"use client";

import React, { useState, useEffect } from "react";
import { METRICS_EVENTS, SpinningWheelUIReward } from "@cede/types";
import { QueryKeys } from "@cede/utils";
import { WheelContainer } from "../WheelContainer";
import { WheelHistory } from "../WheelHistory/WheelHistory";
import { PortalModal } from "@/components/ui/portal-modal";
import { useDevice, useHubUser } from "@/hooks";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useWheelDialog } from "@/hooks/spinningWheel";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";

interface WheelDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Spinning wheel dialog component
 * Works across all platforms (web, mobile, TMA)
 */
export const WheelDialog: React.FC<WheelDialogProps> = ({ open, onClose }) => {
  const [hasSpun, setHasSpun] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { isMobile } = useDevice();
  const wheelDiameter = isMobile ? 280 : 320;
  const queryClient = useQueryClient();
  const { hubUser } = useHubUser();
  const { trackEvent } = useMetricsEvents();

  const { handleClose } = useWheelDialog({
    isOpen: open,
    onClose,
    hasSpun,
  });

  // Handle spin completion
  const handleSpinCompleteInDialog = (rewards: SpinningWheelUIReward[]) => {
    if (rewards && rewards.length > 0) {
      setHasSpun(true);
    }
  };

  // Handle opening history view
  const handleOpenHistory = () => {
    // Track when user opens the spinning wheel history view from dialog
    trackEvent(METRICS_EVENTS.SPINNING_WHEEL_HISTORY_VIEW_OPENED, {
      hubUserId: hubUser?.id,
    });
    setShowHistory(true);
  };

  // Handle closing history view (back button)
  const handleCloseHistory = () => {
    // Track when user clicks back from spinning wheel history view
    trackEvent(METRICS_EVENTS.SPINNING_WHEEL_HISTORY_BACK_CLICKED, {
      hubUserId: hubUser?.id,
    });
    setShowHistory(false);
  };

  // Handle dialog close with state reset
  const handleDialogClose = () => {
    // Track when user closes the spinning wheel dialog
    trackEvent(METRICS_EVENTS.SPINNING_WHEEL_DIALOG_CLOSED, {
      hubUserId: hubUser?.id,
      hadSpun: hasSpun,
      wasInHistoryView: showHistory,
    });
    setHasSpun(false);
    setShowHistory(false);
    queryClient.invalidateQueries({ queryKey: [QueryKeys.SPINNING_WHEEL_RESULTS] });
    handleClose();
  };

  // Handle ESC key to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        handleDialogClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [open]);

  return (
    <PortalModal
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleDialogClose();
      }}
      showCloseButton
      closeButtonClassName={isMobile ? "top-4 right-4" : "top-6 right-6"}
    >
      <PortalModal.Header>
        {showHistory ? (
          <div className="flex items-center px-6 gap-4">
            <button
              onClick={handleCloseHistory}
              className="p-2 rounded-lg hover:bg-cede-primary/10 transition-colors text-cede-text-secondary hover:text-cede-primary cursor-pointer"
              aria-label="Back"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <PortalModal.Title className="text-2xl font-bold text-cede-primary px-0">Win History</PortalModal.Title>
          </div>
        ) : (
          <PortalModal.Title className="text-2xl font-bold text-cede-primary px-0 text-center">
            Spin the Wheel!
          </PortalModal.Title>
        )}
      </PortalModal.Header>

      <PortalModal.Content
        className={cn("relative flex-1 min-h-0 flex flex-col", isMobile ? "pt-4" : "pt-2")}
        scrollable
      >
        {!showHistory ? (
          <div className="flex items-center justify-center">
            <WheelContainer
              onSpinComplete={handleSpinCompleteInDialog}
              onClose={handleDialogClose}
              diameter={wheelDiameter}
              onViewHistory={handleOpenHistory}
            />
          </div>
        ) : (
          <WheelHistory />
        )}
      </PortalModal.Content>

      {/* Global Styles - Required for dynamically created elements */}
      <style jsx global>{`
        .wavy {
          animation-name: wavy;
          animation-duration: 1.3s;
          animation-timing-function: ease;
          animation-iteration-count: infinite;
          position: relative;
          top: 0;
          left: 0;
        }

        @keyframes wavy {
          0% {
            top: 0px;
          }
          50% {
            top: -15px;
          }
          100% {
            top: 0px;
          }
        }

        .fade-out-collapse {
          animation: fadeOutCollapse 0.5s ease-out forwards;
        }

        .fade-in {
          animation: fadeIn 0.5s ease-in forwards;
        }

        @keyframes fadeOutCollapse {
          0% {
            opacity: 1;
            transform: translateY(0);
            max-height: 200px;
            margin-bottom: 2rem;
          }
          50% {
            opacity: 0;
            transform: translateY(-20px);
            max-height: 200px;
            margin-bottom: 2rem;
          }
          100% {
            opacity: 0;
            transform: translateY(-20px);
            max-height: 0;
            margin-bottom: 0;
          }
        }

        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: translateY(-20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </PortalModal>
  );
};
